import { Component, type ErrorInfo, type ReactNode } from "react";
import { SystemState } from "../components/SystemState";

type Props = { children: ReactNode };
type State = { failed: boolean; requestId?: string };

function currentRoute() {
  return window.location.pathname.slice(0, 200);
}

export function reportClientError(input: {
  code: string;
  module: string;
  requestId?: string;
}) {
  const payload = JSON.stringify({
    code: input.code.slice(0, 80),
    module: input.module.slice(0, 80),
    requestId: input.requestId?.slice(0, 128),
    route: currentRoute(),
  });
  void fetch("/api/client-errors", {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: payload,
    keepalive: true,
  }).catch(() => undefined);
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(error: unknown): State {
    const requestId = error && typeof error === "object" && "requestId" in error
      ? String((error as { requestId?: unknown }).requestId ?? "") || undefined
      : undefined;
    return { failed: true, requestId };
  }

  componentDidCatch(error: unknown, _info: ErrorInfo) {
    void _info;
    reportClientError({
      code: error && typeof error === "object" && "code" in error
        ? String((error as { code?: unknown }).code ?? "FRONTEND_RENDER_ERROR")
        : "FRONTEND_RENDER_ERROR",
      module: "react.error_boundary",
      requestId: this.state.requestId,
    });
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <SystemState
        mode="page"
        tone="danger"
        title="Bu ekran şu anda açılamıyor"
        description="Beklenmeyen bir görüntüleme sorunu oluştu. Çalışmanızın teknik ayrıntıları güvenli biçimde kaydedildi; sayfayı yenileyerek devam edebilirsiniz."
        primaryAction={{ label: "Sayfayı yeniden yükle", onClick: () => window.location.reload() }}
        secondaryAction={{ label: "Ana ekrana dön", onClick: () => window.location.assign("/") }}
        reference={this.state.requestId}
        assistance="Sorun tekrarlanırsa işlem kodunu Bilgi Teknolojileri ekibiyle paylaşın."
      />
    );
  }
}

export function installGlobalErrorReporting() {
  window.addEventListener("error", () => {
    reportClientError({ code: "FRONTEND_UNHANDLED_ERROR", module: "window.error" });
  });
  window.addEventListener("unhandledrejection", () => {
    reportClientError({ code: "FRONTEND_UNHANDLED_REJECTION", module: "window.promise" });
  });
}
